import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import backIcon from '../Assets/arrow-back.png';
import { api } from '../api';
import './SearchResults.css';

function SearchResults() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const query = queryParams.get('q') || '';
  const pageParam = queryParams.get('p');

  const [content_data, setData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [resultsCount, setTotalResults] = useState(0);
  const [docArticles, setdocArticles] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!query || !query.trim()) {
      setdocArticles([]);
      setTotalResults(0);
      setTotalPages(0);
      setCurrentPage(1);
      return;
    }
    const page = pageParam && /^\d+$/.test(pageParam) ? parseInt(pageParam, 10) : 1;
    const url = `https://api.tablemate.net/search.php?q=${encodeURIComponent(query.trim())}&p=${page}`;
    api.fetch(url)
      .then(async (response) => {
        const text = await response.text();
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        try {
          return JSON.parse(text);
        } catch {
          throw new Error('Invalid response');
        }
      })
      .then((content_data) => {
        setError(null);
        setData(content_data);
        const articles = (Array.isArray(content_data) ? content_data : [])
          .filter((el) => el && (el.status === 1 || el.status === '1'));
        // Sort by relevance descending (highest first)
        const sorted = [...articles].sort((a, b) => (b.relevance ?? 0) - (a.relevance ?? 0));
        renderCategories(sorted);
        setCurrentPage(page);
        const first = content_data[0];
        setTotalPages(first ? (first.pages || 0) : 0);
        setTotalResults(first ? (first.count || 0) : 0);
      })
      .catch((err) => {
        console.error('Error fetching search:', err);
        setError(err.message);
        setdocArticles([]);
        setTotalResults(0);
        setTotalPages(0);
      });
  }, [location, query, pageParam]);

  const getRelevanceColor = (score, minScore, maxScore) => {
    if (minScore == null || maxScore == null || maxScore === minScore) return '#94a3b8';
    const t = (score - minScore) / (maxScore - minScore);
    const lerp = (a, b, x) => Math.round(a + (b - a) * x);
    const r = lerp(220, 22, t);
    const g = lerp(38, 163, t);
    const b = lerp(38, 74, t);
    return `rgb(${r}, ${g}, ${b})`;
  };

  const renderCategories = (list) => {
    const arr = Array.isArray(list) ? list : [];
    const scores = arr.map((a) => a.relevance ?? 0).filter((s) => s > 0);
    const minScore = scores.length ? Math.min(...scores) : 0;
    const maxScore = scores.length ? Math.max(...scores) : 0;

    const items = arr.map((art) => {
      const src = art.application === 'Front End Mobile Application' ? 'f' : art.application === 'Back End Mobile Application' ? 'b' : 's';
      const relScore = art.relevance ?? 0;
      const bgColor = getRelevanceColor(relScore, minScore, maxScore);
      const textColor = '#fff';
      return (
        <li key={art.nid} className='details-link'>
          <Link
            to={`/${art.category_code}/kb/${art.nid}?s=${src}&src=search`}
            state={{ category: art.category_code }}
          >
            <span>{art.title}</span>
          </Link>
          {art.relevance != null && (
            <span className='search-relevance' title='How well this result matches your search' style={{ backgroundColor: bgColor, color: textColor }}>Match: {Math.min(100, Math.max(0, art.relevance ?? 0))}%</span>
          )}
          {art.summary && <div className='search-summary'>{art.summary}</div>}
          <div className='search-application'>
            Application: <i><Link className='details-link' to={`/search?q=${encodeURIComponent(art.application || '')}`}>{art.application}</Link></i>
          </div>
          {art.tags && (
            <div className='search-tags'>Tags: {
              art.tags.split(',').map((tag, i) => (
                <React.Fragment key={i}>
                  {i > 0 && ', '}
                  <Link to={`/search?q=${encodeURIComponent(tag.trim())}`}>{tag.trim()}</Link>
                </React.Fragment>
              ))
            }</div>
          )}
          <hr />
        </li>
      );
    });
    setdocArticles(items);
  };

  const handlePageChange = (newPage) => {
    navigate(`/search?q=${encodeURIComponent(query)}&p=${newPage}`);
  };

  const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    const pages = Math.max(0, Math.ceil(totalPages));
    return (
      <div className="search-pagination">
        {Array.from({ length: pages }, (_, i) => i + 1).map((p) => (
          <button
            key={p}
            type="button"
            className={currentPage === p ? 'active' : ''}
            disabled={currentPage === p}
            onClick={() => onPageChange(p)}
          >
            {p}
          </button>
        ))}
      </div>
    );
  };


  return (
    <div className="RetaurantCheckin">
      <div className="details-container-page">
        <div className='details-container-page-title'>
          <Link to="/">
            <button type="button" className="back-button">
              <img src={backIcon} alt="Back" />
            </button>
          </Link>
          <h3 className='detailTitle'>
            {query ? `${resultsCount} Search Results For: ${query}` : 'Search'}
          </h3>
        </div>
        {error && <p style={{ color: '#c00', padding: '1rem' }}>{error}</p>}
        <ul className="doc-article-list">
          {docArticles}
        </ul>
        {totalPages > 1 && (
          <>
            <div>Page {currentPage} of {totalPages}</div>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </>
        )}
      </div>
    </div>
  );
}

export default SearchResults